/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext, createContext, useMemo } from 'react'
import { StepType } from '@wiggot/web-components/tour/types'

import {
  getSeenSearchTour,
  postSeenSearchTour
} from '@/services/search.services'
import { ITourSearchWizzard } from './types'
import Transition from '@dd3tech/wiggui/Transition'
import WiggotXMonopolio from '@/assets/svg/wiggot-x-monopolio.svg'
import Image from 'next/image'
import { useTour } from '@wiggot/web-components/tour'
import useMatchWindowQuery from '@wiggot/web-components/hooks/ds/useMatchWindowQuery'
import Button from '@dd3tech/wiggui/Button'

const defaultValues: ITourSearchWizzard = {
  initWizzard: () => null
}

const TourSearchWizzardContext =
  createContext<ITourSearchWizzard>(defaultValues)

export interface IProps {
  children: React.ReactNode
  mapboxGLIsSupported?: boolean
  onFinishTour?: () => void
  onActiveWizard?: () => void
  onClickMap?: () => void
}

const TourSearchWizzardProvider = ({
  children,
  mapboxGLIsSupported,
  onFinishTour,
  onActiveWizard,
  onClickMap
}: IProps) => {
  const [hasSeenSearchTour, setHasSeenSearchTour] = useState(true)
  const [openWizzard, setOpenWizzard] = useState(false)
  const [openFirstScreen, setOpenFirstScreen] = useState(false)
  const [openLastScreen, setOpenLastScreen] = useState(false)
  const { setIsOpen, setSteps, setCurrentStep, currentStep, isOpen, steps } =
    useTour()
  const { isMD } = useMatchWindowQuery()

  const baseStylesPopover = {
    backgroundColor: '#222222',
    color: '#fff',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '12px'
  }

  const tourStepsMobile: StepType[] = [
    {
      selector: '.tour-search-step-mb-1',
      content:
        'Configura tu búsqueda seleccionando el tipo de operación, dirección, precio y tipo de propiedad.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 28
        })
      },
      action: () => {
        const mapButton = document.getElementById('show-map-button')
        if (mapButton) {
          const className = mapButton.className
          mapButton.className = `${className} !hidden`
        }
      }
    },
    {
      selector: '.tour-search-step-2',
      content: 'Usa los filtros para refinar tu búsqueda.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 9999
        })
      }
    },
    {
      selector: '.tour-search-step-2-1',
      content:
        'Busca rápidamente entre propiedades de Wiggot o todo el mercado.',
      padding: {
        mask: 10
      }
    },
    {
      selector: '.tour-search-step-2-2',
      content: 'Despliega para conocer datos de la zona.'
    },
    {
      selector: '[data-testid="property-card"] div',
      content:
        'Explora los resultados de tu búsqueda y visualiza la información más destacada rápidamente.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 16
        })
      }
    },
    {
      selector: '.tour-search-step-2-3',
      content:
        'Ubica propiedades en el mapa y agrégalas a listas para compartir con usuarios, clientes u otros asesores.',
      action: () => {
        const mapButton = document.getElementById('show-map-button')
        if (mapButton) {
          mapButton.classList.remove('!hidden')
        }
      }
    },
    {
      selector: '.tour-search-step-mb-3',
      content: 'Ve al mapa',
      action: () => {
        onClickMap?.()
        const wizardContainerInfo: HTMLElement =
          document.getElementsByClassName(
            'wizard-container-info'
          )[0] as HTMLElement
        if (wizardContainerInfo) {
          wizardContainerInfo.style.display = 'none'
        }
        setTimeout(() => {
          setCurrentStep((step) => (step === steps?.length - 1 ? 0 : step + 1))
          wizardContainerInfo.style.display = 'block'
        }, 1000)
      }
    },
    {
      selector: '#listings-svg-container',
      content: 'Ubica los resultados de tu búsqueda en el mapa.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 0
        })
      }
    },
    {
      selector: '.tour-search-step-4',
      content: 'Dibuja la zona en la que quieres realizar una búsqueda.',
      styles: {
        popover: (base: { [key: string]: string }): React.CSSProperties => ({
          ...base,
          ...baseStylesPopover,
          left: -14
        })
      }
    }
  ]

  const tourStepsMD: StepType[] = [
    {
      selector: '.tour-search-step-1',
      content:
        'Configura tu búsqueda seleccionando el tipo de operación, dirección, precio y tipo de propiedad.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 28
        })
      }
    },
    {
      selector: '.tour-search-step-2',
      content: 'Usa los filtros para refinar tu búsqueda.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 22
        })
      }
    },
    {
      selector: '.tour-search-step-2-1',
      content:
        'Busca rápidamente entre propiedades de Wiggot o todo el mercado.',
      padding: {
        mask: 10
      }
    },
    {
      selector: '.tour-search-step-2-2',
      content: 'Despliega para conocer datos de la zona.'
    },
    {
      selector: '[data-testid="property-card"] div',
      content:
        'Explora los resultados de tu búsqueda y visualiza la información más destacada rápidamente.',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 16
        })
      }
    },
    {
      selector: '.tour-search-step-2-3',
      content:
        'Ubica propiedades en el mapa y agrégalas a listas para compartir con usuarios, clientes u otros asesores.'
    },
    {
      selector: '#listings-svg-container',
      content: 'Ubica los resultados de tu búsqueda en el mapa.',
      styles: {
        popover: (base: { [key: string]: string }): React.CSSProperties => {
          const properties = document
            .getElementById('listings-svg-container')
            ?.getBoundingClientRect()

          return {
            ...base,
            ...baseStylesPopover,
            transform: 'translate(0, 0)',
            left: Number(properties?.left) - 367,
            top: Number(properties?.top)
          }
        }
      }
    },
    {
      selector: '.tour-search-step-4',
      content: 'Dibuja la zona en la que quieres realizar una búsqueda.',
      styles: {
        popover: (base: { [key: string]: string }): React.CSSProperties => ({
          ...base,
          ...baseStylesPopover,
          left: -14
        })
      }
    },
    {
      selector: '.tour-search-step-6',
      content:
        'Si necesitas ver este recorrido nuevamente, puedes hacerlo desde aquí',
      styles: {
        maskArea: (base: {
          [key: string]: string
        }): React.CSSProperties & { rx?: number } => ({
          ...base,
          rx: 40
        }),
        popover: (base: { [key: string]: string }): React.CSSProperties => ({
          ...base,
          ...baseStylesPopover,
          left: -14
        })
      }
    }
  ]
  const getTout = () => {
    const currentTour = isMD ? tourStepsMD : tourStepsMobile
    if (!mapboxGLIsSupported) {
      return isMD
        ? [
            ...currentTour.slice(0, 3),
            ...currentTour.slice(4, 6),
            currentTour[currentTour.length - 1]
          ]
        : [...currentTour.slice(0, 6)]
    }
    return currentTour
  }
  const tourSteps = getTout()

  const verifyHasSeenSearchTour = async () => {
    try {
      const response = await getSeenSearchTour()
      if (response?.data && response?.data?.value == null) {
        setHasSeenSearchTour(false)
        initWizzard()
      }
    } catch (error) {
      console.error(error)
    }
    return 0
  }

  useEffect(() => {
    verifyHasSeenSearchTour()
  }, [])

  const initWizzard = () => {
    setOpenFirstScreen(true)
    setOpenWizzard(true)
  }

  const initTour = () => {
    onActiveWizard?.()
    setOpenFirstScreen(false)

    if (setSteps) {
      setSteps(tourSteps)
    }

    setCurrentStep(0)
    setIsOpen(true)

    if (!hasSeenSearchTour) {
      postSeenSearchTour()
      setHasSeenSearchTour(true)
    }
  }

  const finishTour = () => {
    onFinishTour?.()
    setOpenFirstScreen(false)
    setOpenLastScreen(false)
    setIsOpen(false)
    setOpenWizzard(false)

    if (!hasSeenSearchTour) {
      postSeenSearchTour()
      setHasSeenSearchTour(true)
    }
  }

  useEffect(() => {
    if (openWizzard && !isOpen && currentStep === tourSteps.length - 1) {
      setOpenLastScreen(true)
    }
  }, [isOpen, currentStep])

  const values = useMemo(
    () => ({
      initWizzard
    }),
    [initWizzard]
  )

  return (
    <TourSearchWizzardContext.Provider value={values}>
      {children}
      {openWizzard && (openFirstScreen || openLastScreen) && (
        <div className="flex flex-row justify-center items-center fixed top-0 left-0 w-full h-full bg-surface-dark/[.75] backdrop-blur-[2px] z-10">
          <div className="max-w-[552px]">
            <Transition className="!flex flex-col gap-3" show={openFirstScreen}>
              <div className="w-full flex justify-center">
                <Image src={WiggotXMonopolio} alt="monopolio" />
              </div>

              <div className="font-semibold text-white text-[32px] text-center">
                Hemos mejorado tu experiencia buscando propiedades.
              </div>
              <div className="font-semibold text-white text-base text-center">
                Monopolio y Wiggot ahora te permiten ver métricas de mercado y
                toda la oferta del mercado en un solo lugar
              </div>
              <div className="flex flex-row justify-center gap-2">
                <Button
                  variant="link"
                  className="text-white"
                  onClick={finishTour}
                >
                  Saltar guía
                </Button>
                <Button variant="terciary" onClick={initTour}>
                  Explorar
                </Button>
              </div>
            </Transition>
            <Transition className="!flex flex-col gap-3" show={openLastScreen}>
              <div className="font-semibold text-white text-[32px] text-center">
                Explora y descubre las mejores oportunidades
              </div>
              <div className="flex flex-row justify-center gap-2">
                <Button variant="terciary" onClick={finishTour}>
                  Realizar búsqueda
                </Button>
              </div>
            </Transition>
          </div>
        </div>
      )}
    </TourSearchWizzardContext.Provider>
  )
}

export { TourSearchWizzardProvider }

export const useTourSearchWizzard = (): ITourSearchWizzard =>
  useContext(TourSearchWizzardContext)
